import { siteTypes } from "@/features/routing/config";
import { buildLocalePath } from "@/lib/locale";
import type { LocaleCode } from "@/lib/labels/types";
import type { PageType } from "@/features/routing/types";
import { ID_PARAM_NAMES, idToUrlSegment } from "@/utilities/mappers/idUrlFormat";

/** Minimum post shape needed to build a detail-page URL. */
export interface PostLike {
    type?: string | null;
    slug?: string | null;
}

/**
 * URL Builder — the typed object returned by createUrlBuilder().
 * Every method returns a fully-qualified, locale-prefixed URL string.
 */
export interface UrlBuilder {
    home(): string;
    standings(competitionId?: string): string;
    team(teamId: string): string;
    player(playerId: string): string;
    match(matchId: string): string;
    /**
     * Sport-aware event URL. Dispatches by sport to the correct route:
     * football → /football/match/:matchId, basketball → /basketball/match/:matchId, …
     */
    sportEvent(sport: "football" | "basketball" | "tennis" | "hockey" | "american-football", matchId: string): string;
    matchesList(competitionId?: string): string;
    squad(): string;
    article(slug: string): string;
    articles(): string;
    /** Post detail URL that respects the post's type ("video" → /videos/:slug, otherwise /articles/:slug). */
    post(post: PostLike): string;
    author(slug: string): string;
    category(slug: string): string;
    categoryPage(slug: string, page: number): string;
    video(slug: string): string;
    videos(): string;
    predictor(competitionId?: string): string;
    footballCompetition(competitionId?: string): string;
    footballCompetitions(): string;
    competitionNews(competitionId?: string): string;
    competitionDraw(competitionId?: string): string;
    competitionVideos(competitionId?: string): string;
    teamsList(competitionId?: string): string;
    players(): string;
    livescore(): string;
    search(query?: string): string;
    signIn(): string;
    signUp(): string;
    forgotPassword(): string;
    profile(): string;
    /** Locale-prefixed base path: "/en" for multi-lang, "" for single-lang */
    basePath: string;
}

/**
 * Replace `:param` placeholders in a path template with URI-encoded values.
 * Throws if a placeholder has no matching key in `params`.
 */
function fillTemplate(template: string, params: Record<string, string>): string {
    return template.replace(/:([a-zA-Z]+)/g, (_placeholder, key) => {
        const value = params[key];
        if (value === undefined) {
            throw new Error(`Missing param "${key}" for URL template "${template}"`);
        }
        if (ID_PARAM_NAMES.has(key)) {
            return idToUrlSegment(value);
        }
        return encodeURIComponent(value);
    });
}

/**
 * Pure factory that creates a URL builder for a given site type and locale context.
 *
 * Works on both server and client — no React hooks, no side effects.
 *
 * @param siteType      - "news-blog" or "football-competition-site"
 * @param locale        - BCP-47 locale code (e.g. "en", "de", "pt-BR")
 * @param isMultiLang   - true when tenant has >1 enabled locale
 * @param competitionId - default competition ID (required for news-blog competition routes)
 */
export function createUrlBuilder(
    siteType: string,
    locale: LocaleCode,
    isMultiLang: boolean,
    competitionId?: string | null
): UrlBuilder {
    const config = siteTypes[siteType];
    if (!config) {
        throw new Error(`Unknown site type: "${siteType}"`);
    }

    // Build a lookup: page type → path template (first match wins)
    const pathMap = new Map<string, string>();
    for (const route of config.routes) {
        if (route.path && !pathMap.has(route.page)) {
            pathMap.set(route.page, route.path);
        }
    }

    // Path template for paginated category URLs (the second /category route).
    const categoryPaginatedPath = config.routes.find(
        (r) => r.page === "category" && r.path?.includes(":page")
    )?.path;

    /** Resolve a full locale-prefixed URL for a page type with optional params. */
    function buildUrl(page: PageType, params?: Record<string, string>): string {
        const template = pathMap.get(page);
        if (!template) {
            throw new Error(`No path template for page "${page}" in site type "${siteType}"`);
        }
        const raw = params ? fillTemplate(template, params) : template;
        return buildLocalePath(raw, locale, isMultiLang);
    }

    /** Resolve the effective competition ID (override or default). */
    function resolveCompetitionId(override?: string): string {
        const id = override ?? competitionId;
        if (!id) {
            throw new Error("competitionId is required but was not provided");
        }
        return id;
    }

    /** Build params object, only including competitionId when the template needs it. */
    function competitionParams(page: PageType, cid?: string): Record<string, string> | undefined {
        const template = pathMap.get(page);
        if (template && template.includes(":competitionId")) {
            return { competitionId: resolveCompetitionId(cid) };
        }
        return undefined;
    }

    return {
        home: () => buildUrl("home"),
        standings: (cid?) => buildUrl("standings", competitionParams("standings", cid)),
        team: (teamId) => buildUrl("team", { teamId }),
        player: (playerId) => buildUrl("player", { playerId }),
        match: (matchId) => buildUrl("match", { matchId }),
        sportEvent: (sport, matchId) => {
            switch (sport) {
                case "football": return buildUrl("match", { matchId });
                case "basketball": return buildUrl("basketballEvent", { matchId });
                case "tennis": return buildUrl("tennisEvent", { matchId });
                case "hockey": return buildUrl("hockeyEvent", { matchId });
                case "american-football": return buildUrl("americanFootballEvent", { matchId });
            }
        },
        matchesList: (cid?) => buildUrl("matchesList", competitionParams("matchesList", cid)),
        squad: () => buildUrl("squad"),
        article: (slug) => buildUrl("article", { slug }),
        articles: () => buildUrl("articles"),
        post: (post) => {
            const slug = post.slug ?? "";
            return post.type === "video"
                ? buildUrl("video", { slug })
                : buildUrl("article", { slug });
        },
        author: (slug) => buildLocalePath(`/authors/${encodeURIComponent(slug)}`, locale, isMultiLang),
        category: (slug) => buildUrl("category", { slug }),
        categoryPage: (slug, page) => {
            if (page <= 1 || !categoryPaginatedPath) {
                return buildUrl("category", { slug });
            }
            const raw = fillTemplate(categoryPaginatedPath, { slug, page: String(page) });
            return buildLocalePath(raw, locale, isMultiLang);
        },
        video: (slug) => buildUrl("video", { slug }),
        videos: () => buildUrl("videos"),
        predictor: (cid?) => buildUrl("predictor", competitionParams("predictor", cid)),
        footballCompetition: (cid?) => buildUrl("footballCompetition", { competitionId: resolveCompetitionId(cid) }),
        footballCompetitions: () => buildUrl("footballCompetitions"),
        competitionNews: (cid?) => buildUrl("competitionNews", competitionParams("competitionNews", cid)),
        competitionDraw: (cid?) => buildUrl("competitionDraw", competitionParams("competitionDraw", cid)),
        competitionVideos: (cid?) => buildUrl("competitionVideos", competitionParams("competitionVideos", cid)),
        teamsList: (cid?) => buildUrl("teamsList", competitionParams("teamsList", cid)),
        players: () => buildUrl("players"),
        livescore: () => buildUrl("livescore"),
        search: (query?: string) => {
            const base = buildUrl("search");
            return query ? `${base}?q=${encodeURIComponent(query)}` : base;
        },
        signIn: () => buildUrl("signIn"),
        signUp: () => buildUrl("signUp"),
        forgotPassword: () => buildUrl("forgotPassword"),
        profile: () => buildUrl("profile"),
        basePath: buildLocalePath("", locale, isMultiLang),
    };
}
